import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../App';
import api from '../utils/api';
import LoadingState from '../components/LoadingState';

const Attributes = () => {
    const navigate = useNavigate();
    const { showSuccess, showError } = useToast();
    const [attributes, setAttributes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingAttribute, setEditingAttribute] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        enable_archives: false,
        order_by: 'menu_order',
    });

    useEffect(() => {
        loadAttributes();
    }, []);

    const loadAttributes = async () => {
        try {
            setLoading(true);
            const response = await api.getAttributes();
            if (response.success) {
                setAttributes(response.attributes || []);
            }
        } catch (error) {
            showError('Failed to load attributes');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingAttribute) {
                const response = await api.updateAttribute(editingAttribute.id, formData);
                if (response.success) {
                    showSuccess('Attribute updated successfully');
                    loadAttributes();
                    resetForm();
                }
            } else {
                const response = await api.createAttribute(formData);
                if (response.success) {
                    showSuccess('Attribute created successfully');
                    loadAttributes();
                    resetForm();
                }
            }
        } catch (error) {
            showError(error.message || 'Failed to save attribute');
        }
    };

    const handleEdit = (attribute) => {
        setEditingAttribute(attribute);
        setFormData({
            name: attribute.name,
            slug: attribute.slug,
            enable_archives: attribute.enable_archives || false,
            order_by: attribute.order_by || 'menu_order',
        });
        setShowForm(true);
    };

    const handleDelete = async (attributeId) => {
        if (!confirm('Are you sure you want to delete this attribute? All its terms will also be deleted.')) return;

        try {
            const response = await api.deleteAttribute(attributeId);
            if (response.success) {
                showSuccess('Attribute deleted successfully');
                loadAttributes();
            }
        } catch (error) {
            showError('Failed to delete attribute');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            slug: '',
            enable_archives: false,
            order_by: 'menu_order',
        });
        setEditingAttribute(null);
        setShowForm(false);
    };

    const handleConfigureTerms = (attribute) => {
        // Navigate to terms page with attribute ID
        navigate(`/attributes/${attribute.id}/terms`, { state: { attribute } });
    };

    return (
        <div className="page attributes-page">
            <div className="page-container">
                <div className="page-header">
                    <div className="page-title">
                        <h1>Attributes</h1>
                        <p>Manage product attributes</p>
                    </div>
                    <div className="page-actions">
                        <button 
                            className="btn btn-primary"
                            onClick={() => setShowForm(!showForm)}
                        >
                            {showForm ? 'Cancel' : '+ Add Attribute'}
                        </button>
                    </div>
                </div>

                {showForm && (
                    <div className="taxonomy-form-section">
                        <h3>{editingAttribute ? 'Edit Attribute' : 'Add New Attribute'}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Name *</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        required
                                        placeholder="e.g., Color, Size, Material"
                                    />
                                    <small className="field-description">Name for the attribute (shown on the front-end)</small>
                                </div>
                                <div className="form-group">
                                    <label>Slug</label>
                                    <input
                                        type="text"
                                        value={formData.slug}
                                        onChange={(e) => setFormData({...formData, slug: e.target.value})}
                                        placeholder="Auto-generated from name"
                                    />
                                    <small className="field-description">Unique slug/reference for the attribute</small>
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={formData.enable_archives}
                                            onChange={(e) => setFormData({...formData, enable_archives: e.target.checked})}
                                        />
                                        <span>Enable archives?</span>
                                    </label>
                                    <small className="field-description">Enable this to show a link to an archive page for this attribute</small>
                                </div>
                                <div className="form-group">
                                    <label>Default sort order</label>
                                    <select
                                        value={formData.order_by}
                                        onChange={(e) => setFormData({...formData, order_by: e.target.value})}
                                    >
                                        <option value="menu_order">Custom ordering</option>
                                        <option value="name">Name</option>
                                        <option value="name_num">Name (numeric)</option>
                                        <option value="id">Term ID</option>
                                    </select>
                                    <small className="field-description">Determines the sort order of the terms on the frontend</small>
                                </div>
                            </div>
                            <div className="form-actions">
                                <button type="submit" className="btn btn-primary">
                                    {editingAttribute ? 'Update' : 'Create'} Attribute
                                </button>
                                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div className="taxonomy-list">
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <LoadingState message="Loading attributes..." />
                        </div>
                    ) : attributes.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">⚙️</div>
                            <h3>No attributes yet</h3>
                            <p>Create your first attribute to get started</p>
                        </div>
                    ) : (
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Slug</th>
                                    <th>Terms</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {attributes.map(attribute => (
                                    <tr key={attribute.id}>
                                        <td><strong>{attribute.name}</strong></td>
                                        <td>{attribute.slug}</td>
                                        <td>
                                            <button 
                                                className="btn btn-sm btn-primary"
                                                onClick={() => handleConfigureTerms(attribute)}
                                            >
                                                Configure terms
                                            </button>
                                        </td>
                                        <td>
                                            <button 
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => handleEdit(attribute)}
                                            >
                                                Edit
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-danger"
                                                onClick={() => handleDelete(attribute.id)}
                                                style={{marginLeft: '8px'}}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Attributes;
